import { useEffect, useRef } from "react";

/**
 * useDebouncedEffect(fn, deps, delay)
 *
 * Like useEffect, but `fn` only runs `delay` ms after the LAST deps change.
 * If deps change again before the timer fires (typical on page load: mount
 * fires with address undefined, then the wallet connects and fires again),
 * the pending run is cancelled and rescheduled — so the burst collapses
 * into a single call instead of two or three.
 */
export function useDebouncedEffect(fn, deps, delay = 400) {
    useEffect(() => {
        const timer = setTimeout(fn, delay);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

/**
 * useRpcGuard()
 *
 * Returns `guard(key, fn)`: runs the async `fn` only if no call with the
 * same key is currently in flight. Use it to stop overlapping duplicates
 * of the same fetch (e.g. two effects both triggering the same loader).
 */
export function useRpcGuard() {
    const inFlight = useRef(new Set());
    return async (key, fn) => {
        if (inFlight.current.has(key)) return;
        inFlight.current.add(key);
        try {
            return await fn();
        } finally {
            inFlight.current.delete(key);
        }
    };
}
